<!-- Componente principal da aplicação, ponto de entrada, integra componentes filhos que lidam com diferentes partes-->
<!-- Este componente requisita a info ao backend, através de chamadas com axios aos diferentes endpoints -->
<!-- Depois, essa info é distribuida aos componentes filhos -->

<template>
  <div class="app-container">
    <!-- Header (216px = 108 vazio + 108 com conteúdo) -->
    <header class="app-header d-flex overflow-hidden">
      <div class="header-left-half">
        <img src="@/assets/logoCarris2.png" alt="Logo CARRIS" class="header-logo" />
      </div>

      <!-- Bloco central — empilhado verticalmente -->
      <div class="header-center-stack">
        <div class="bus-stop-id roboto-bold">{{ busStopId }}</div>
        <div class="bus-stop-name roboto-bold">{{ busStopNameTrim }}</div>
        <span class="current-hour roboto-bold">{{ currentHour }}</span>
      </div>

      <div class="header-right-half">
        <img src="@/assets/cml.png" alt="Logo CMLisboa" class="header-cmllogo" />
      </div>
    </header>

    <!-- Body (756px) -->
    <div class="app-body d-flex">
      <!-- Coluna Esquerda (960px) — Lista de carreiras -->
      <div class="left-column">
        <div v-if="busStopError" class="error-left-column">
          <div v-if="busStopErrorType === '404'">
            <img src="@/assets/404.png" alt="Ícone de erro 404" class="errorImage d-block mx-auto" />
          </div>
          <div v-else-if="parseInt(busStopErrorType) >= 500">
            <img src="@/assets/ServerDown.png" alt="Ícone de erro 500" class="errorImage d-block mx-auto" />
          </div>
        </div>

        <div v-else>
          <BusStopArrivalTimesAlternative
            v-if="isRouteColorsReady"
            :busstopnumber="busstopnumber"
            :bus-stop-details="busStopDetails"
            :routeColors="routeColors"
            :arrivalTimes="arrivalTimes"
          />
        </div>
      </div>

      <!-- Coluna Direita (960px) — Header amarelo + Mapa -->
      <div class="right-column">
        <div class="right-column-body">
          <div v-if="busStopDetails && busPositionsRefreshInterval">
            <BusPositions
              :bus-stop-name="busStopName"
              :bus-stop-latitude="busStopLatitude"
              :bus-stop-longitude="busStopLongitude"
              :bus-stop-number="busstopnumber"
              :routeColors="routeColors"
              :busPositionsInterval="busPositionsRefreshInterval"
              :painelId="painelId"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Footer (108px) -->
    <!-- <footer class="app-footer">
      <AlertsSection :alerts="alerts" />
    </footer> -->
  </div>
</template>

<script>
import { getAlerts, getArrivalTimes, getBusStopConfig, getBusStopInfo } from '@/services/api.js';
import { io } from 'socket.io-client';
import BusStopArrivalTimesAlternative from './ArrivalTimesAlternative.vue';
import BusPositions from './BusPositions.vue';
// import AlertsSection from './ShowAlerts.vue';
import { setDebugMode } from '@/config/config.js';
import { getEnv } from '@/config/runtimeEnv';
import html2canvas from 'html2canvas';

export default {
  name: 'InfoScreen',
  props: {
    busstopnumber: {
      type: String,
      required: true,
    },
    painelId: {
      type: String,
      required: false,
      default: null,
    },
  },

  components: {
    // AlertsSection,
    BusStopArrivalTimesAlternative,
    BusPositions,
  },

  provide() {
    return {
      logDebug: this.logDebug,
    };
  },

  data() {
    return {
      currentHour: new Date().toLocaleTimeString('pt', { hour: '2-digit', minute: '2-digit' }),
      error: null,
      busStopId: '',
      busStopName: '',
      busStopLatitude: null,
      busStopLongitude: null,
      isLoadingBusStop: true,
      busStopError: '',
      busStopErrorType: '',
      timeInterval: null,
      alertsInterval: null,
      weatherInterval: null,
      busStopDetails: null,
      alerts: [],
      arrivalTimes: [],
      routeColors: {},
      isRouteColorsReady: false,
      arrivalTimesInterval: null,
      isFetchingAlerts: false,
      isFetchingArrivalTimes: false,
      isFetchingBusStopDetails: false,
      lastConfig: null,
      busPositionsRefreshInterval: null,
      weatherRefreshInterval: null,
      reloadWidget: true,
      socket: null,
      painelAutorizado: null,
      debugModeFilter: { client: false, server: false },
    };
  },

  computed: {
    busStopNameTrim() {
      const name = this.busStopName.trim();
      if (name.length > 32) {
        return name.substring(0, 29) + '...';
      } else {
        return this.busStopName;
      }
    },
  },

  async mounted() {
    this.isRouteColorsReady = true;
    this.currentHour = new Date().toLocaleTimeString('pt', { hour: '2-digit', minute: '2-digit' });
    this.fetchRefreshIntervals();

    if (this.painelId) {
      await this.initWebSocket();
    }
  },

  beforeUnmount() {
    clearInterval(this.timeInterval);
    clearInterval(this.alertsInterval);
    clearInterval(this.arrivalTimesInterval);
    clearInterval(this.busStopDetailsInterval);
    clearInterval(this.refreshInterval);
    if (this.socket) this.socket.disconnect();
  },

  methods: {
    logDebug(message, typeMessage) {
      if (this.debugModeFilter.client) {
        if (typeMessage === 'error') {
          console.error(message);
        } else {
          console.log(message);
        }
      }
      if (this.debugModeFilter.server) {
        if (this.socket) {
          this.socket.emit('debugMessage', {
            panelId: this.painelId,
            busStop: this.busstopnumber,
            message: message,
            messageType: typeMessage || 'info',
            timestamp: new Date(),
          });
        }
      }
    },

    async initWebSocket() {
      this.socket = await io(getEnv('VUE_APP_API_WEBSOCKET_URL', '/painel-jcdecaux-api/painel'), {
        transports: ['websocket'],
        reconnection: false,
        query: {
          panelId: this.painelId,
          busStopNumber: this.busstopnumber,
          feVersion: getEnv('VUE_APP_VERSION', '0.0.0'),
        },
        path: getEnv('VUE_APP_API_WEBSOCKET_PATH', '/painel-jcdecaux-api/socket.io'),
      });

      this.socket.on('authorized', (data) => {
        console.log(`Websocket authorized: ${data.busStopNumber} e painel: ${data.panelId}`);
        this.logDebug('Websocket authorized:  painel' + data.panelId + ' autorizado com acesso à paragem ' + data.busStopNumber);
        this.painelAutorizado = true;
      });

      this.socket.on('refreshBusStop', (data) => {
        this.logDebug('refreshBusStop: ' + data.panelId);
        this.fetchArrivalTimes();
        this.fetchBusStopDetails();
        this.fetchAlerts();
        this.reloadWeatherWidget();
        this.socket.emit('refreshBusStopAck', {
          panelId: data.panelId,
          busStop: this.busstopnumber,
          timestamp: new Date(),
        });
      });

      this.socket.on('reload', (data) => {
        this.logDebug('reload: ' + data.panelId);
        this.socket.emit('reloadAck', {
          panelId: data.panelId,
          busStop: this.busstopnumber,
          timestamp: new Date(),
        });
        window.location.reload();
      });

      this.socket.on('isActive', (data) => {
        this.logDebug('isActive: ' + data.panelId);
        this.socket.emit('isActiveAck', {
          panelId: data.panelId,
          busStop: this.busstopnumber,
          timestamp: new Date(),
        });
      });

      this.socket.on('debugMode', (data) => {
        this.debugModeFilter = {
          client: data.mode.active && data.mode.modes?.includes('client'),
          server: data.mode.active && data.mode.modes?.includes('server'),
        };

        setDebugMode(this.debugModeFilter.client);
        this.logDebug('debugMode: ' + JSON.stringify(data));

        this.socket.emit('debugModeAck', {
          panelId: data.panelId,
          busStop: this.busstopnumber,
          timestamp: new Date(),
        });
      });

      this.socket.on('env:init', (env) => {
        this.logDebug('Variáveis de ambiente recebidas: ' + JSON.stringify(env));
        this.applyConfigVariables(env);
      });

      this.socket.on('env:update', (env) => {
        this.logDebug('Configurações de refresh recebidas: ' + JSON.stringify(env));
        this.applyConfigVariables(env);
      });

      this.socket.on('connect_error', (err) => {
        console.warn('Erro de ligação WebSocket, a apresentar dados sem WebSocket:', err.message);
        this.logDebug('connect_error: ' + JSON.stringify(err));
      });

      this.socket.on('error', (data) => {
        console.warn('Painel inexistente, a apresentar dados sem WebSocket:', data.message);
        this.logDebug('error: ' + JSON.stringify(data));
      });

      this.socket.on('disconnect', (reason) => {
        console.log('WebSocket desligado:', reason);
        this.logDebug('disconnect: ' + reason);
      });

      this.socket.on('requestScreenshot', async () => {
        this.logDebug('requestScreenshot recebido, a tirar screenshot');

        try {
          //document body captura o ecrã inteiro
          const canvas = await html2canvas(document.body, {
            scale: 1,
            logging: false,
            useCORS: true, // tenta carregar imagens cross-origin (mapas, logótipos)
          });
          const screenshotDataUrl = canvas.toDataURL('image/jpeg', 0.6);

          this.socket.emit('screenshotResult', {
            dataUrl: screenshotDataUrl,
          });
          this.logDebug('Screenshot tirada e enviada com sucesso');
        } catch (error) {
          console.error('Erro ao tirar screenshot:', error);
          this.socket.emit('screenshotResult', { error: error.message });
        }
      });
    },

    async fetchArrivalTimes() {
      this.logDebug('fetchArrivalTimes para a paragem ' + this.busstopnumber);
      try {
        if (this.isFetchingArrivalTimes) return;

        this.isFetchingArrivalTimes = true;
        this.isLoading = true;

        if (!this.busstopnumber) {
          console.error('Número da paragem inexistente.');
          this.logDebug('fetchArrivalTimes: número da paragem inexistente.', 'error');
          this.isLoading = false;
          return;
        }
        const response = await getArrivalTimes(this.busstopnumber, this.painelId);

        if (response && Array.isArray(response.arrivalTimes)) {
          this.arrivalTimes = response.arrivalTimes.map((item, index) => {
            const arrival = item.arrivalTime;

            return {
              id: arrival.id !== undefined ? arrival.id : index,
              routeNumber: arrival.routeNumber,
              routeName: arrival.routeName.length > 32 ? arrival.routeName.substring(0, 29).toUpperCase() + '...' : arrival.routeName.toUpperCase(),
              destinationName:
                arrival.destinationName.length > 32
                  ? arrival.destinationName.substring(0, 29).toUpperCase() + '...'
                  : arrival.destinationName.toUpperCase(),
              estimatedArrivalTimeLeft: item.arrivalTime.estimatedArrivalTimeLeft,
              hasAlert: arrival.hasAlert,
            };
          });

          this.routeColors = {};
          response.arrivalTimes.forEach((item) => {
            const arrival = item.arrivalTime;
            const zone = item.zone;
            if (zone && arrival && arrival.routeNumber) {
              this.routeColors[arrival.routeNumber] = zone.color;
            }
          });

          this.busStopError = false;
          this.busStopErrorType = '';
        }
      } catch (error) {
        this.logDebug('fetchArrivalTimes: ' + error, 'error');
        if (error.response && error.response.status >= 500) {
          // tratamento
        } else if (error.response && error.response.status == 404) {
          // tratamento
        } else {
          console.error('Erro:', error);
          this.logDebug('fetchArrivalTimes: Erro ao buscar os horários de chegada. ' + error, 'error');
        }
        this.errorMessage = error.response?.error || error.message;
      } finally {
        this.isLoading = false;
        this.isFetchingArrivalTimes = false;
      }
    },

    async fetchBusStopDetails() {
      try {
        this.logDebug('fetchBusStopDetails');
        if (this.isFetchingBusStopDetails) return;
        this.isFetchingBusStopDetails = true;
        if (!this.busstopnumber) {
          this.logDebug('fetchBusStopDetails: número da paragem inexistente.', 'error');
          return;
        }

        const response = await getBusStopInfo(this.busstopnumber, this.painelId);

        if (response && response.busStopInfo) {
          const { publicId, name, lat, lng } = response.busStopInfo;
          this.busStopDetails = { publicId, name, lat, lng };

          this.busStopId = publicId;
          this.busStopName = name;
          this.busStopLatitude = lat;
          this.busStopLongitude = lng;

          this.busStopError = '';
        }
      } catch (error) {
        console.error(error);
        this.busStopDetailsError = 'Não foi possível obter os detalhes da paragem.';
        this.logDebug('fetchBusStopDetails: Não foi possível obter os detalhes da paragem. ' + error, 'error');
      } finally {
        this.isLoadingBusStop = false;
        this.isFetchingBusStopDetails = false;
      }
    },

    async fetchAlerts() {
      if (this.isFetchingAlerts) return;
      this.isFetchingAlerts = true;

      this.logDebug('getAlerts');
      getAlerts(this.busstopnumber, this.painelId)
        .then((response) => {
          if (response && Array.isArray(response.alerts)) {
            this.alerts = response.alerts;
          } else if (Array.isArray(response)) {
            this.alerts = response;
          } else {
            this.logDebug('getAlerts: Formato inesperado dos alertas. ' + response, 'error');
          }
        })
        .catch((error) => {
          this.logDebug('getAlerts: Erro ao ir buscar os alertas. ' + error, 'error');
        })
        .finally(() => {
          this.isFetchingAlerts = false;
        });
    },

    async reloadWeatherWidget() {
      this.logDebug('WeatherWidget');
      this.reloadWidget = false;
      await new Promise((resolve) => setTimeout(resolve, 100));
      this.reloadWidget = true;
      this.logDebug('WeatherWidget concluído');
    },

    async applyConfigVariables(env) {
      if (!env) {
        this.logDebug('applyConfigVariables: Nenhuma variável de ambiente recebida para aplicar.', 'error');
        return;
      }

      this.logDebug('A aplicar variáveis de ambiente: ' + JSON.stringify(env));

      if (env.FRONTEND_TIME_INTERVAL) this.timeInterval && clearInterval(this.timeInterval);
      if (env.FRONTEND_ALERTS_INTERVAL) this.alertsInterval && clearInterval(this.alertsInterval);
      if (env.FRONTEND_ARRIVAL_TIMES_INTERVAL) this.arrivalTimesInterval && clearInterval(this.arrivalTimesInterval);
      if (env.FRONTEND_BUSSTOP_INTERVAL) this.busStopDetailsInterval && clearInterval(this.busStopDetailsInterval);
      if (env.FRONTEND_BUS_POSITIONS_INTERVAL) this.busPositionsRefreshInterval = env.FRONTEND_BUS_POSITIONS_INTERVAL;
      if (env.FRONTEND_WEATHER_INTERVAL) this.weatherRefreshInterval = env.FRONTEND_WEATHER_INTERVAL;

      this.lastConfig = null;
      this.fetchRefreshIntervals();
      this.reloadWeatherWidget();
    },

    async fetchRefreshIntervals() {
      try {
        const config = await getBusStopConfig(this.busstopnumber, this.painelId);

        if (this.lastConfig && JSON.stringify(this.lastConfig) === JSON.stringify(config)) {
          this.logDebug('getBusStopConfig: Configurações inalteradas, a saltar reset dos valores dos intervalos');
          return;
        } else {
          this.logDebug('getBusStopConfig: Configurações alteradas, a actualizar os valores dos intervalos');
        }

        this.lastConfig = config;

        this.busPositionsRefreshInterval = config.busPositionsInterval;
        this.weatherRefreshInterval = config.weatherInterval;
        if (this.refreshInterval) clearInterval(this.refreshInterval);
        if (this.timeInterval) clearInterval(this.timeInterval);
        if (this.alertsInterval) clearInterval(this.alertsInterval);
        if (this.arrivalTimesInterval) clearInterval(this.arrivalTimesInterval);
        if (this.busStopDetailsInterval) clearInterval(this.busStopDetailsInterval);
        if (this.weatherInterval) clearInterval(this.weatherInterval);

        this.refreshInterval = setInterval(() => {
          this.fetchRefreshIntervals();
        }, config.configInterval);

        this.timeInterval = setInterval(() => {
          this.currentHour = new Date().toLocaleTimeString('pt', { hour: '2-digit', minute: '2-digit' });
        }, config.timeInterval);

        this.fetchAlerts();
        this.alertsInterval = setInterval(() => {
          this.fetchAlerts();
        }, config.alertsInterval);

        this.fetchArrivalTimes();
        this.arrivalTimesInterval = setInterval(() => {
          this.fetchArrivalTimes();
        }, config.arrivalTimesInterval);

        this.fetchBusStopDetails();
        this.busStopDetailsInterval = setInterval(() => {
          this.fetchBusStopDetails();
        }, config.busStopInterval);

        this.weatherInterval = setInterval(() => {
          this.reloadWeatherWidget();
        }, config.weatherInterval);

        this.logDebug('Intervalos actualizados com as configurações do backend.');
      } catch (error) {
        console.error('Erro ao buscar as configurações de refresh:', error);
        this.logDebug('fetchRefreshIntervals: Erro ao buscar as configurações de refresh. ' + error, 'error');
      }
    },
  },
};
</script>

<style>
* {
  box-sizing: border-box;
}

/* ==================================================
   Container principal — 1920 x 1080 fixo
   ================================================== */
.app-container {
  width: 1920px;
  height: 1080px;
  margin: 0 auto;
}

/* ==================================================
   HEADER — 216px
   ================================================== */
.app-header {
  width: 1920px;
  height: 216px;
  background-color: #fbd700;
}

.header-center-stack {
  display: flex;
  flex-direction: column; /* empilha os filhos na vertical */
  justify-content: center; /* centra verticalmente dentro do header */
  align-items: center; /* centra horizontalmente */
  flex-grow: 1; /* ocupa o espaço entre os dois logos */
  height: 100%;
  gap: 8px; /* espaço entre as 3 linhas — ajusta a gosto */
}

.app-header .header-left-half,
.app-header .header-right-half {
  width: 960px;
  height: 100%;
  display: flex;
  align-items: center;
}

.app-header .header-left-half {
  padding-left: 50px;
}

.app-header .header-cmllogo {
  /* height: 60px;
  width: auto; */
  justify-content: flex-end;
}

/* .app-header .header-logo {
  height: 50px;
  width: auto;
} */

.app-header .header-right-half {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 50px;
}

.current-hour {
  font-size: 34px;
  white-space: nowrap;
  line-height: 1;
}

/* ==================================================
   BODY — 864px (1080 - 216 header)
   ================================================== */
.app-body {
  width: 1920px;
  height: 864px;
}

.error-container {
  height: 864px;
  width: 100%;
}

/* Coluna Esquerda — 960px x 864px */
.left-column {
  width: 960px;
  height: 864px;
  overflow: hidden;
}

.error-left-column img {
  max-width: 90%;
  min-height: 90%;
}

.right-column {
  width: 960px;
  height: 864px;
  padding-top: 10px;
}

.bus-stop-info {
  gap: 30px;
  text-align: justify;
}

.bus-stop-id {
  font-size: 34px;
  font-family: 'Roboto', sans-serif;
  font-weight: bold;
  white-space: nowrap;
}

.bus-stop-name {
  font-size: 34px;
  font-family: 'Roboto', sans-serif;
  font-weight: bold;
  white-space: nowrap;
  text-align: justify;
}

.busstop-icon img {
  width: 30px;
  height: 30px;
}

.right-column-body {
  width: 960px;
  height: 864px;
  background-color: #f0f0f0;
}

/* Imagens de erro */
.errorImage {
  width: 720px;
}

/* Loading */
.loading-container {
  width: 1920px;
  height: 864px;
  background-color: #111;
}

.loading-content {
  gap: 32px;
}

.loading-bus-img {
  width: 160px;
  animation: bus-bounce 1s ease-in-out infinite alternate;
  filter: brightness(0) invert(1);
}

.loading-track {
  width: 400px;
  height: 6px;
}

.loading-track-line {
  width: 100%;
  height: 4px;
  background-color: #333;
  border-radius: 2px;
}

.loading-track-dot {
  top: -5px;
  width: 14px;
  height: 14px;
  background-color: #fbd700;
  border-radius: 50%;
  animation: track-slide 1.4s ease-in-out infinite;
}

.loading-text {
  color: #fbd700;
  font-size: 28px;
  font-family: 'Roboto', sans-serif;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  animation: text-pulse 1.4s ease-in-out infinite;
}

@keyframes bus-bounce {
  from {
    transform: translateY(0px);
  }
  to {
    transform: translateY(-12px);
  }
}

@keyframes track-slide {
  0% {
    left: 0;
  }
  100% {
    left: calc(100% - 14px);
  }
}

@keyframes text-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}
</style>
